<template>
  <page-template id="userDetails">

    <user-detail-navbar
        :user="userData"
        :active="state === 'ready'"
        :is-read-only="isReadOnly"
        @delete-user="deleteRequested = true"
        @resetPassword="showChangeUserPassword = true"/>

    <the-overlay :active="state === 'loading'">

      <error-group
          :title="$t('adm.error.title')"
          :message="$t('adm.error.USER_NOT_FOUND')"
          v-if="state === 'notFound'"/>

      <div class="h-split" v-if="state !== 'loading'">

        <div class="container-fluid content">
          <user-details
              :user-form="userForm"
              :org-data="organization"
              :managed-as-primary-contact="userData.managedAsPrimaryContact"
              :crm-id="userData.crmId"
              ref="userDetails"
              @update:firstname="onChange('firstname', $event)"
              @update:lastname="onChange('lastname', $event)"
              @update:lang="onChange('preferredLanguage', $event)"
              @update:pin="onChange('pin', $event)"
              @update:birthdate="onChange('birthdate', $event)"
              @update:roles="onChange('roles', $event)"
              @update:organization="onOrganizationSelect"
              @update:admin-level="onChange('adminRole', $event)"
              @update:managed-organizations="onChange('managedOrganizations', $event)"/>
          <hr/>
          <admins-panel
              :primary-contact="organization.primaryContact"
              :admins="userAdmins"
              @user-click="gotoUser"
              class="admins-group"
              v-if="$arraySize(userAdmins) > 0"/>
          <br/>
          <history-panel :history="userData.history" v-if="$arraySize(userData.history) > 0"/>
        </div>

        <licenses-editor
            ref="licenseEditor"
            :org-licenses="organization.licenses"
            :user-licenses="userData.licenses"
            :license-definitions="organizationLicenses"
            :is-read-only="isReadOnly"
            @update-selection="onChange('licenses', $event)"
            @save-changes="saveChanges">
          <input
              type="button"
              class="btn btn-primary w-100"
              :value="$t('adm.button.save')"
              :disabled="isReadOnly || changedFields.size === 0"
              @click="saveChanges"
              @keyup.enter="saveChanges"/>
        </licenses-editor>
      </div>

    </the-overlay>

    <confirmation-modal
        id="c-usr-del"
        v-if="deleteRequested"
        :title="$t('adm.page.deleteUser.title')"
        :confirm="$t('adm.page.deleteUser.confirmDelete')"
        @cancel="deleteRequested = false"
        @confirm="deleteUser"/>
    <change-user-password-modal
        id="change-pwd"
        v-if="showChangeUserPassword"
        :uid="userData.id"
        @changed="showChangeUserPassword = false"
        @cancel="showChangeUserPassword = false"/>

  </page-template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { RouteLocationNormalized } from 'vue-router';
import { Logger } from 'oidc-client-ts';
import PageTemplate from '@/components/layout/PageTemplate.vue';
import TheOverlay from '@/components/ui/TheOverlay.vue';
import Backend, {
  AdminDTO,
  AdminLevel, BackendError,
  Change, CPF,
  LicenseData,
  OrganizationDetails, PIN,
  QueryOptions,
  UserAttr,
  UserData,
  userDisplayName,
} from '@/components/backend';
import HistoryPanel from '@/views/Users/edit/HistoryPanel.vue';
import { i18n } from '@/lang';
import ConfirmationModal from '@/components/ui/ConfirmationModal.vue';
import ErrorGroup from '@/components/ui/ErrorGroup.vue';
import { mapState } from 'pinia';
import useAppStore from '@/components/store';
import UserDetailNavbar from './UserDetailNavbar.vue';
import LicensesEditor from './LicensesEditor.vue';
import UserDetails from './UserDetails.vue';
import AdminsPanel from '@/views/Organizations/view/AdminsPanel.vue';
import ChangeUserPasswordModal from './ChangeUserPasswordModal.vue';

/**
 * form used for user create & edit
 */
export interface UserForm /* extends CreateUserRequest */
{
  id?: string; // for edit only
  email: string;
  firstname: string;
  lastname: string;
  preferredLanguage?: string;
  organization: string;
  roles?: string[];
  pin: PIN;
  birthdate?: string; // YYYY-MM-DD
  adminLevel?: AdminLevel;
  managedOrganizations?: string[];
  licenses?: string[];
  crmId?: string;
  syncFlag?: boolean;
}

export default defineComponent({
  name: 'EditUserPage',
  components: {
    ErrorGroup,
    AdminsPanel,
    ChangeUserPasswordModal,
    ConfirmationModal,
    HistoryPanel,
    LicensesEditor,
    PageTemplate,
    UserDetailNavbar,
    UserDetails,
    TheOverlay,
  },
  data() {
    return {
      state: 'loading' as 'loading' | 'ready' | 'err' | 'notFound',
      userForm: {} as UserForm,
      userData: {} as UserData, // read-only
      organization: {} as OrganizationDetails,
      organizationLicenses: [] as LicenseData[],
      admins: [] as AdminDTO[],
      // userHistory: [] as HistoryItem[],
      changedFields: new Map<UserAttr, any>(), // {} as Record<string,Change<UserAttr>[]>,
      deleteRequested: false,
      showChangeUserPassword: false,
    };
  },
  computed: {
    ...mapState(useAppStore, {
      canEditUser: 'canEditUser',
    }),

    userAdmins(): AdminDTO[] | undefined {
      return this.admins?.filter((adm) => adm.adminRole === 'userAdmin');
    },

    isReadOnly(): boolean {
      return !this.canEditUser(this.userData.adminLevel);
    },
  },
  methods: {

    compUserDetails(): typeof UserDetails {
      return (this.$refs.userDetails as typeof UserDetails);
    },

    compLicenseEditor(): typeof LicensesEditor {
      return this.$refs.licenseEditor as typeof LicensesEditor;
    },

    onOrganizationSelect(oid: string) {
      // this.user.organization = oid;
      Backend.validateToken().then((api) => this.loadOrganization(api, oid));

      if (this.userData.organization === oid) {
        this.changedFields.delete('organization');
      } else {
        this.changedFields.set('organization', oid);
      }
    },

    onChange(fieldName: UserAttr, val: any) {
      const field = fieldName === 'adminRole' ? 'adminLevel' : fieldName;
      const dirty = this.isDirty(field, this.userData[field], val);
      if (dirty) {
        this.changedFields.set(fieldName, val);
      } else {
        this.changedFields.delete(fieldName);
      }
      /* Logger.debug(
        'EditUser',
        `on-change(${field}, old:${this.userData[field]}, new:${val}, dirty: ${dirty})`,
        Array.from(this.changedFields),
      ); */
    },

    isDirty(field: string, oldVal: any, newVal: any): boolean {
      // Logger.debug("EditUser",`is-dirty( old:${oldVal}, new:${newVal})`)
      if (field === 'roles' || field === 'managedOrganizations' || field === 'licenses') {
        return !this.$equalSets(oldVal, newVal);
      }
      if (field === 'pin') {
        // console.log(`old pin: ${JSON.stringify(oldVal)}  new pin: ${JSON.stringify(newVal)}`);
        return oldVal !== newVal && newVal !== null && (oldVal == null || newVal.value !== oldVal.value);
      }
      return oldVal !== newVal;
    },

    calcChanges(): Change<UserAttr>[] {
      if (this.changedFields.size === 0) return [];

      const list = new Array<Change<UserAttr>>();

      this.changedFields.forEach((v, k) => {
        if (k === 'adminRole' && v === 'none') {
          list.push({ op: 'remove', field: 'adminRole' });
        } else {
          list.push({ op: 'replace', field: `${k}`, value: v });
        }
      });
      // Logger.debug("EditUser","all-changes:", list ? JSON.stringify(list) : '-');
      return list;
    },

    gotoUser(uid: string) {
      this.$router.push(`/${i18n.global.locale}/members/${uid}`);
    },

    async loadOrganization(api: Backend, oid: string) {
      // Logger.info("EditUser","loading org by id:", this.user.orgRef.id)

      if (!oid) {
        Logger.error('EditUser', 'user organization missing!');
        this.$toast.error(this.$t('adm.error.generic'));
        this.state = 'err';
        return;
      }

      this.state = 'loading';
      api.loadOrganization(oid, { $expand: 'licenses,admins', $locale: i18n.global.locale } as QueryOptions)
          .then((r1) => {
            Logger.debug('EditUser', 'org loaded');
            this.organization = r1.data;
            this.organizationLicenses = r1.extra?.licenses ?? [];
            this.admins = r1.extra?.admins ?? [];
            this.state = 'ready';
          })
          .catch(this.handleError);
    },

    async loadUserData(id: string) {
      // Logger.info("EditUser","loading user by id:", id)
      this.state = 'loading';
      this.$toast.clear();

      Backend.validateToken().then((api) => api
          .loadUser(id, { $expand: 'licenses' } as QueryOptions)
          .then((r) => {
            Logger.debug('EditUser', 'user loaded');
            this.userData = r.data;
            this.userForm = {
              ...r.data,
              adminLevel: r.data.adminLevel ?? 'none',
              roles: [...this.userData.roles ?? new Array<string>()],
              managedOrganizations: [...r.data.managedOrganizations ?? []], // deep copy
              pin: (r.data.pin ? { ...r.data.pin } : new CPF('')),
            };

            this.loadOrganization(api, this.userData.organization);
          })
          .catch(this.handleError));
    },

    async saveChanges() {
      Logger.debug('EditUser', 'saving changes...');
      this.$toast.clear();

      // this.calcChanges();
      if (this.changedFields.size === 0 || !this.compUserDetails()?.isValid()) return;

      const req: Change<UserAttr>[] = this.calcChanges();

      this.state = 'loading';
      Backend.validateToken().then((api) => api
          .updateUser(this.userData.id as string, req)
          .then(() => {
            Logger.debug('EditUser', 'saved.');

            // this.$router.back();
            this.$toast.success(this.$t('adm.page.userDetail.infoSuccess'), { duration: 5000 });
            this.goBack1Page();

            // this.loadUserData(this.$route.params.id as string)
          })
          .catch(this.handleError));
    },

    deleteUser() {
      Backend.validateToken().then((api) => api.deleteUser(this.userData.id as string)
          .then(() => {
            this.$toast.success(this.$t('adm.page.deleteUser.infoSuccess', [userDisplayName(this.userData)]));
            this.goBack1Page();
          }).catch(() => this.$toast.error(this.$t('adm.error.generic'))));
    },

    async handleError(e: BackendError) {
      Logger.error('EditUser', e);

      if (e.response?.status === 400 && e.restError?.code === 'INVALID_PARAMETER' && e.restError?.invalidField === 'birthdate') {
        this.$toast.error(this.$t('adm.error.invalidBirthDate'));
        this.state = 'err';
      } else if (e.response?.status === 404 && e.restError?.code === 'USER_NOT_FOUND') {
        this.state = 'notFound';
      } else if (e.response?.status === 403) {
        this.$router.push('/access-denied');
      } else {
        this.state = 'err';
        this.$toast.error(Backend.errorMessage(e, this.$t));
      }
    },
  },
  mounted() {
    // Logger.debug("EditUser",`mounted`);
    this.loadUserData(this.$route.params.id as string);
  },
  beforeRouteUpdate(to: RouteLocationNormalized) {
    // Logger.debug("EditUser",`beforeRouteUpdate`, to);
    this.loadUserData(to.params.id as string);
  },
});
</script>

<style>
</style>
